import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const defaultOptions = {
  validate: null,
  onError: null,
};

export default function useFormHandler(defaultValues = {}, options = defaultOptions) {
  const { validate, onError } = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options],
  );

  const defaultValuesRef = useRef(defaultValues);
  const defaultValuesSignatureRef = useRef(JSON.stringify(defaultValues));

  const [values, setValues] = useState({ ...defaultValues });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const signature = JSON.stringify(defaultValues);
    if (signature !== defaultValuesSignatureRef.current) {
      defaultValuesRef.current = defaultValues;
      defaultValuesSignatureRef.current = signature;
      setValues({ ...defaultValues });
      setErrors({});
      setTouched({});
    }
  }, [defaultValues]);

  const setFieldValue = useCallback((name, value) => {
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const setFieldTouched = useCallback((name, isTouched = true) => {
    setTouched((prev) => ({
      ...prev,
      [name]: isTouched,
    }));
  }, []);

  const handleChange = useCallback(
    (event) => {
      const { name, type, checked, value } = event.target;
      const nextValue = type === "checkbox" ? checked : value;

      setFieldValue(name, nextValue);
    },
    [setFieldValue],
  );

  const register = useCallback(
    (name, { id, transform } = {}) => ({
      name,
      id: id || name,
      value: transform ? transform(values[name]) : values[name] ?? "",
      onChange: handleChange,
      onBlur: () => setFieldTouched(name, true),
    }),
    [handleChange, setFieldTouched, values],
  );

  const reset = useCallback(
    (nextValues = defaultValuesRef.current) => {
      setValues({ ...nextValues });
      setErrors({});
      setTouched({});
      defaultValuesRef.current = nextValues;
      defaultValuesSignatureRef.current = JSON.stringify(nextValues);
    },
    [],
  );

  const runValidation = useCallback(
    (currentValues) => {
      if (validate) {
        return validate(currentValues) || {};
      }
      return {};
    },
    [validate],
  );

  const handleSubmit = useCallback(
    (onSubmit) => async (event) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      const validationErrors = runValidation(values);
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        setTouched(
          Object.keys(validationErrors).reduce(
            (acc, name) => ({
              ...acc,
              [name]: true,
            }),
            {},
          ),
        );
        onError?.(validationErrors);
        return;
      }

      return onSubmit?.(values, { reset });
    },
    [onError, reset, runValidation, values],
  );

  return {
    values,
    setValues,
    errors,
    setErrors,
    touched,
    setTouched,
    register,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    reset,
    runValidation,
  };
}
